import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";
import { useAppDispatch } from "@/store/store";
import { useCartStore } from "@/store/cart/cart-store";
import { readSafeCartItems, selectCartItems } from "@/store/cart/cart.selectors";
import type { MenuItemInterface } from "@/models/menu-item";
import type { Discount } from "@/models/discount";
import { previewOrder } from "@/services/preview-order";
import { setBillingPreview } from "@/slices/final-billing-slice";
import { useSafeQuery } from "@/hooks/use-safe-query";
import { WEBSITE_ORDER_CHANNEL } from "@/constants/orderChannel";
import { getRestaurantId } from "@/config/restaurant-id";
import { usePublicTenant } from "@/context/PublicTenantProvider";
import { roundPrice } from "@/utils/app-helper";
import { buildPricingLiveFingerprint, pricingPreviewQueryKey } from "@/query-keys/pricing-preview.keys";
import type { BillingPreviewUI } from "@/domain/billing/billing-preview.model";
import type { PricingPreviewView, PricingStatus } from "@/domain/pricing/pricing-preview.model";
import {
  commitConfirmationFromSuccessfulPreviewHttp,
  commitStagedPricingConfirmation,
  stagePricingConfirmationAfterPreviewSuccess,
  type PricingConfirmation,
  needsPricingConfirmationRecovery,
} from "@/domain/pricing/pricing-confirmation";
import { mapBillingPreviewToPricingView } from "@/domain/pricing/map-billing-preview-to-pricing-view";
import { computeNextCartSavingsHint, selectStableNextSavings } from "@/selectors/cartSelectors";
import { buildCartPricingGateContext } from "@/domain/cart/build-cart-pricing-gate";
import type { CartPricingGateContext } from "@/domain/cart/cart-validity.types";

function useDebouncedString(value: string, delayMs: number): string {
  const [debounced, setDebounced] = React.useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

type PreviewParamsRef = {
  selected: Discount | undefined;
  menuItems: MenuItemInterface[];
  userName: string;
  orderType: number;
  orderChannel: number;
  /** PLATFORM_SLUG — middleware identity for public billing preview. */
  slug: string;
};

function useLatestPreviewParamsRef(value: PreviewParamsRef) {
  const r = useRef(value);
  r.current = value;
  return r;
}

export type UsePricingPreviewParams = {
  enabled: boolean;
  userName: string;
  orderType?: number;
  orderChannel?: number;
  debounceMs?: number;
  /** Invalidates preview when the default delivery address identity changes. */
  addressFingerprint?: string;
  /** Discriminates fulfillment mode when multiple modes exist (must match preview inputs). */
  deliveryModeFingerprint?: string;
  /** Delivery / pickup slot identity when the product supports scheduled orders. */
  scheduledSlotFingerprint?: string;
  /** Catalog fingerprint for place-order cross-check (validity authority). */
  catalogFingerprint?: string;
};

export type UsePricingPreviewResult = {
  /** Latest successful backend snapshot (no display dampening). */
  pricingCommitted: PricingPreviewView | null;
  /**
   * Network InFlight ∨ debounce gap (live fingerprint ≠ committed).
   * With keepPreviousData, Display may still be Showing(previous) while this is true → “Updating price…”.
   */
  isSyncing: boolean;
  /**
   * Settle label for UX/gates — see {@link PricingStatus}. Not {@link PricingConfirmation}.
   */
  pricingStatus: PricingStatus;
  /**
   * Display Showing(previous) after Network Failed for the current attempt
   * (`error` set while prior payload retained via keepPreviousData).
   */
  isUsingStalePricing: boolean;
  error: Error | undefined;
  refetch: (opts?: { force?: boolean }) => Promise<void>;
  raw: BillingPreviewUI | undefined;
  /** Live input fingerprint (pre-debounce) — evolving epoch candidate. */
  liveFingerprint: string;
  /** Committed input epoch (debounced fingerprint) driving the Preview query key. */
  committedFingerprint: string;
  pricingGate: CartPricingGateContext;
  /**
   * Owned BackendConfirmation for a successful Preview HTTP (epoch + confirmedAt).
   * Null when only a transport-memo hydrate seeded Display, or after Expire / disable.
   * Does not imply ongoing price correctness.
   */
  pricingConfirmation: PricingConfirmation | null;
  /**
   * Publish a successful Preview HTTP that ran outside this hook's fetcher
   * (Cart Continue) into the owned confirmation — same stage/commit invariants.
   * Returns false when epoch/queryKey do not match the committed sync identity.
   */
  publishConfirmationFromSuccessfulPreviewHttp: (input: {
    payableAmount: number;
    previewEpoch: string;
    previewQueryKey: string;
    confirmedAt?: number;
  }) => boolean;
};

/**
 * Authoritative cart pricing via {@link previewOrder}, debounced and cached (`pricing-preview|…` safe-query key).
 * Syncs successful payloads into Redux `finalBilling.billingPreview` for legacy consumers (projection only).
 *
 * Pricing Freshness ADR: this hook (via {@link import("@/hooks/use-billing-preview-sync").useBillingPreviewSync} /
 * BillingPreviewSyncProvider) owns input epoch, network scheduling, and {@link PricingConfirmation}.
 * The safe-query memory entry is a transport memo only — memo hits do not create confirmation.
 * Phase 4: when debounce-settled and idle without a live-epoch confirmation (and not failed),
 * automatically `refetch({ force: true })` so Place Order cannot deadlock on memo-only Display.
 * Cart Continue may publish a successful Preview HTTP into the same confirmation owner via
 * {@link UsePricingPreviewResult.publishConfirmationFromSuccessfulPreviewHttp} to avoid a
 * duplicate force Preview when epoch/queryKey already match.
 *
 * Prefer {@link useBillingPreviewSync} / `BillingPreviewSyncProvider` as the app-level entry point
 * so discount↔preview synchronization is not owned by UI components.
 */
export function usePricingPreview({
  enabled,
  userName,
  orderType = 3,
  orderChannel = WEBSITE_ORDER_CHANNEL,
  debounceMs = 400,
  addressFingerprint = "",
  deliveryModeFingerprint = "",
  scheduledSlotFingerprint = "",
  catalogFingerprint = "",
}: UsePricingPreviewParams): UsePricingPreviewResult {
  const dispatch = useAppDispatch();
  const { slug: tenantSlug, entryMode } = usePublicTenant();
  const items = useCartStore(selectCartItems);
  const coupons = useSelector((s: RootState) => s.discounts);
  const menuItems = useSelector((s: RootState) => s.menuItems?.list ?? []) as MenuItemInterface[];
  const list = useSelector((s: RootState) => s.discounts?.list ?? []);

  const discountId = coupons?.selected?.discount_id ?? "";
  const slug = (tenantSlug ?? "").trim();
  const tenantReady = Boolean(slug) || entryMode === "custom_hostname";

  const liveFp = useMemo(
    () =>
      buildPricingLiveFingerprint({
        cartLines: items,
        discountId,
        menuItems,
        addressFingerprint,
        orderType,
        orderChannel,
        deliveryModeFingerprint,
        scheduledSlotFingerprint,
      }),
    [
      items,
      discountId,
      menuItems,
      addressFingerprint,
      orderType,
      orderChannel,
      deliveryModeFingerprint,
      scheduledSlotFingerprint,
    ]
  );

  const debouncedFp = useDebouncedString(liveFp, debounceMs);
  // Cache key segmentation only — not HTTP tenant authority (C).
  const rid = getRestaurantId();

  const queryKey = useMemo(() => pricingPreviewQueryKey(rid, userName, debouncedFp), [rid, userName, debouncedFp]);

  const fetchEnabled = Boolean(enabled && queryKey && userName.trim() && items.length > 0 && tenantReady);

  const paramsRef = useLatestPreviewParamsRef({
    selected: coupons?.selected,
    menuItems,
    userName,
    orderType,
    orderChannel,
    slug,
  });

  const epochRef = useRef(debouncedFp);
  epochRef.current = debouncedFp;
  const liveFpRef = useRef(liveFp);
  liveFpRef.current = liveFp;
  const queryKeyRef = useRef(queryKey);
  queryKeyRef.current = queryKey;

  const pendingConfirmationRef = useRef<PricingConfirmation | null>(null);
  const [pricingConfirmation, setPricingConfirmation] = useState<PricingConfirmation | null>(null);

  const { data, error, isLoading, refetch } = useSafeQuery<BillingPreviewUI>({
    queryKey,
    enabled: fetchEnabled,
    keepPreviousData: true,
    fetcher: async () => {
      // Confirmation is staged only here (Preview HTTP). Cache hits skip this fetcher.
      const epochAtStart = epochRef.current;
      const queryKeyAtStart = queryKeyRef.current;
      // The request body is built from the *latest* inputs below. Inside the debounce window
      // those no longer match this query key (e.g. a refetch right after a Delivery/Pickup
      // switch), so the response would be cached and confirmed under the wrong key. Skip it;
      // the key for the new inputs fetches once the debounce settles.
      if (liveFpRef.current !== epochAtStart) {
        const skipped = new Error("pricing preview skipped: inputs changed since this key");
        skipped.name = "AbortError";
        throw skipped;
      }
      const lines = readSafeCartItems();
      if (!lines.length) {
        throw new Error("Cart is empty");
      }
      const p = paramsRef.current;
      const result = await previewOrder(lines, {
        discounts: { selected: p.selected },
        userName: p.userName,
        orderType: p.orderType,
        orderChannel: p.orderChannel,
        menuItems: p.menuItems,
        slug: p.slug,
      });
      pendingConfirmationRef.current = stagePricingConfirmationAfterPreviewSuccess({
        epochAtStart,
        queryKeyAtStart,
        currentEpoch: epochRef.current,
        currentQueryKey: queryKeyRef.current,
        confirmedAt: Date.now(),
        payableAmount: result.payableAmount,
      });
      return result;
    },
  });

  useEffect(() => {
    if (data) {
      dispatch(setBillingPreview(data));
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (!fetchEnabled) {
      pendingConfirmationRef.current = null;
      setPricingConfirmation(null);
      return;
    }
    const next = commitStagedPricingConfirmation({
      pending: pendingConfirmationRef.current,
      networkIdle: !isLoading,
      hasDisplayPayload: Boolean(data),
      currentEpoch: debouncedFp,
      currentQueryKey: queryKey,
    });
    if (next === undefined) return;
    pendingConfirmationRef.current = null;
    // null = discard superseded pending only; keep any previously owned confirmation.
    if (next === null) return;
    setPricingConfirmation(next);
  }, [fetchEnabled, isLoading, data, debouncedFp, queryKey]);

  /**
   * Phase 4 — confirmation recovery.
   * `refetch({ force: true })` bypasses transport memo only; queryKey is already
   * debounced. Abort/generation guards remain inside runSafeQuery.
   * Gate: debounce settled ∧ idle ∧ not failed ∧ no confirmation for committed epoch.
   */
  useEffect(() => {
    if (
      !needsPricingConfirmationRecovery({
        fetchEnabled,
        liveEpoch: liveFp,
        committedEpoch: debouncedFp,
        networkInFlight: isLoading,
        confirmationRequestFailed: Boolean(error),
        pricingConfirmation,
      })
    ) {
      return;
    }
    void refetch({ force: true });
  }, [
    fetchEnabled,
    liveFp,
    debouncedFp,
    isLoading,
    error,
    pricingConfirmation,
    refetch,
  ]);

  const isSyncing = isLoading || liveFp !== debouncedFp;

  const pricingStatus: PricingStatus = useMemo(() => {
    if (!fetchEnabled) return "optimistic";
    if (isSyncing) return "syncing";
    if (data) return "confirmed";
    return "optimistic";
  }, [fetchEnabled, isSyncing, data]);

  const isUsingStalePricing = Boolean(error && data);

  const nextSavings = useMemo(() => {
    if (!data || !items.length) return null;
    const subR = roundPrice(data.subTotal);
    return selectStableNextSavings(computeNextCartSavingsHint(items, subR, coupons?.selected, list));
  }, [data, items, coupons?.selected, list]);

  const pricingCommitted: PricingPreviewView | null = useMemo(() => {
    if (!items.length || !data) return null;
    return mapBillingPreviewToPricingView(data, nextSavings);
  }, [items.length, data, nextSavings]);

  const pricingGate = useMemo(
    () =>
      buildCartPricingGateContext({
        cartLines: items,
        billingPreview: data ?? null,
        pricingStatus,
        isSyncing,
        isUsingStalePricing,
        liveFingerprint: liveFp,
        committedFingerprint: debouncedFp,
        catalogFingerprint: catalogFingerprint || undefined,
        pricingConfirmation,
      }),
    [
      items,
      data,
      pricingStatus,
      isSyncing,
      isUsingStalePricing,
      liveFp,
      debouncedFp,
      catalogFingerprint,
      pricingConfirmation,
    ]
  );

  const publishConfirmationFromSuccessfulPreviewHttp = useCallback(
    (input: {
      payableAmount: number;
      previewEpoch: string;
      previewQueryKey: string;
      confirmedAt?: number;
    }): boolean => {
      const next = commitConfirmationFromSuccessfulPreviewHttp({
        fetchEnabled,
        liveEpoch: liveFp,
        committedEpoch: debouncedFp,
        committedQueryKey: queryKey,
        previewEpoch: input.previewEpoch,
        previewQueryKey: input.previewQueryKey,
        payableAmount: input.payableAmount,
        confirmedAt: input.confirmedAt ?? Date.now(),
      });
      if (!next) return false;
      pendingConfirmationRef.current = null;
      setPricingConfirmation(next);
      return true;
    },
    [fetchEnabled, liveFp, debouncedFp, queryKey]
  );

  return {
    pricingCommitted,
    isSyncing,
    pricingStatus,
    isUsingStalePricing,
    error,
    refetch,
    raw: data,
    liveFingerprint: liveFp,
    committedFingerprint: debouncedFp,
    pricingGate,
    pricingConfirmation,
    publishConfirmationFromSuccessfulPreviewHttp,
  };
}
